import { useAuth } from "@clerk/react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export const createBlueprintPath = "/blueprints/new";

export function PublicLogoMark() {
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-600 text-lg font-black text-white shadow-sm shadow-indigo-200"
    >
      A
    </span>
  );
}

export function PublicPrimaryLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
    >
      {children}
    </Link>
  );
}

export function PublicSecondaryLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-900 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
    >
      {children}
    </Link>
  );
}

export function PublicHeader() {
  const { isLoaded, isSignedIn } = useAuth();
  const showAuthenticatedActions = isLoaded && isSignedIn;

  return (
    <header className="sticky top-0 z-20 border-b border-indigo-100/80 bg-white/90 backdrop-blur">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          aria-label="Alchemy Blueprint Engine home"
        >
          <PublicLogoMark />
          <span className="min-w-0">
            <span className="block truncate text-base font-bold tracking-tight text-slate-950">
              Alchemy Blueprint Engine
            </span>
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">
              Blueprint Studio
            </span>
          </span>
        </Link>

        <nav
          className="hidden items-center gap-6 text-sm font-semibold text-slate-600 lg:flex"
          aria-label="Primary navigation"
        >
          <Link to="/#how-it-works" className="hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            How it works
          </Link>
          <Link to="/#features" className="hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Features
          </Link>
          <Link to="/pricing" className="hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Pricing
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {showAuthenticatedActions ? (
            <>
              <PublicSecondaryLink to="/dashboard">Dashboard</PublicSecondaryLink>
              <PublicPrimaryLink to={createBlueprintPath}>Create blueprint</PublicPrimaryLink>
            </>
          ) : (
            <>
              <PublicSecondaryLink to="/sign-in">Log in</PublicSecondaryLink>
              <PublicPrimaryLink to="/sign-up">Try free</PublicPrimaryLink>
            </>
          )}
        </div>

        <details className="group relative lg:hidden">
          <summary className="flex cursor-pointer list-none items-center rounded-xl border border-indigo-100 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Menu
          </summary>
          <div className="absolute right-0 mt-3 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-indigo-100 bg-white p-3 shadow-xl">
            <nav className="grid gap-1 text-sm font-semibold text-slate-700" aria-label="Mobile navigation">
              <Link to="/#how-it-works" className="rounded-lg px-3 py-2 hover:bg-indigo-50">
                How it works
              </Link>
              <Link to="/#features" className="rounded-lg px-3 py-2 hover:bg-indigo-50">
                Features
              </Link>
              <Link to="/pricing" className="rounded-lg px-3 py-2 hover:bg-indigo-50">
                Pricing
              </Link>
              <div className="mt-2 grid gap-2 border-t border-slate-100 pt-3">
                {showAuthenticatedActions ? (
                  <>
                    <PublicSecondaryLink to="/dashboard">Dashboard</PublicSecondaryLink>
                    <PublicPrimaryLink to={createBlueprintPath}>Create blueprint</PublicPrimaryLink>
                  </>
                ) : (
                  <>
                    <PublicSecondaryLink to="/sign-in">Log in</PublicSecondaryLink>
                    <PublicPrimaryLink to="/sign-up">Try free</PublicPrimaryLink>
                  </>
                )}
              </div>
            </nav>
          </div>
        </details>
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-slate-500 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <p className="font-semibold text-slate-700">
          Alchemy Blueprint Engine © {new Date().getFullYear()}
        </p>
        <nav className="flex flex-wrap gap-4" aria-label="Footer navigation">
          <Link to="/" className="hover:text-indigo-700">Home</Link>
          <Link to="/pricing" className="hover:text-indigo-700">Pricing</Link>
          <Link to="/sign-in" className="hover:text-indigo-700">Log in</Link>
          <Link to="/privacy" className="hover:text-indigo-700">Privacy</Link>
          <Link to="/terms" className="hover:text-indigo-700">Terms</Link>
        </nav>
      </div>
    </footer>
  );
}

